<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '../../app/store/index.js'
import { useCatalogStore } from '../../app/store/catalog.js'
import { useCartStore } from '../../app/store/cart.js'
import { ROUTES, STORAGE_KEYS } from '../../core/config/constants.js'
import FloatingCartButton from '../components/cart/FloatingCartButton.vue'
import MobileCartFooter from '../components/cart/MobileCartFooter.vue'
import CartDrawer from '../components/cart/CartDrawer.vue'
import UserSidebarSection from '../components/layout/UserSidebarSection.vue'
import SidebarSearch from '../components/layout/SidebarSearch.vue'
import SidebarViewSettings from '../components/layout/SidebarViewSettings.vue'
import CatalogModeChooser from '../components/catalog/CatalogModeChooser.vue'
import { useVersion } from '@/core/composables/useVersion.js'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const catalogStore = useCatalogStore()
const cartStore = useCartStore()
const { version } = useVersion()

const menuOpen = ref(false)
const isCartOpen = ref(false)
const searchQuery = ref('')

// Catalog mode chooser (normal vs immersive), opened from the "קטלוג" menu item.
// A remembered "אל תראה שוב" pick (localStorage) skips the chooser entirely.
const showCatalogChooser = ref(false)

const CATALOG_MODE_ROUTES = {
  normal: ROUTES.CATALOG,
  immersive: ROUTES.CATALOG_IMMERSIVE,
}

function getSavedCatalogMode() {
  try {
    const saved = localStorage.getItem(STORAGE_KEYS.CATALOG_MODE_CHOICE)
    return CATALOG_MODE_ROUTES[saved] ? saved : null
  } catch (_e) {
    return null
  }
}

function saveCatalogMode(mode) {
  try {
    localStorage.setItem(STORAGE_KEYS.CATALOG_MODE_CHOICE, mode)
  } catch (_e) {
    // Storage unavailable (private mode) — the chooser will simply show again.
  }
}

function openCatalogChooser() {
  searchQuery.value = ''
  closeMenu()
  const savedMode = getSavedCatalogMode()
  if (savedMode) {
    router.push({ name: CATALOG_MODE_ROUTES[savedMode] })
    return
  }
  showCatalogChooser.value = true
}

function chooseCatalogMode(mode, remember) {
  if (remember) saveCatalogMode(mode)
  showCatalogChooser.value = false
  router.push({ name: CATALOG_MODE_ROUTES[mode] })
}

function chooseNormalCatalog({ remember } = {}) {
  chooseCatalogMode('normal', remember)
}

function chooseImmersiveCatalog({ remember } = {}) {
  chooseCatalogMode('immersive', remember)
}

// Sidebar section collapse state (admin only) - accordion: only one open at a time
const sectionExpanded = ref({
  agents: true,
  agentsManagement: false,
  b2b: false,
  catalog: false,
  system: false,
  clientApps: false,
})

function toggleSection(key) {
  const isOpen = sectionExpanded.value[key]
  // Close all sections
  for (const k in sectionExpanded.value) {
    sectionExpanded.value[k] = false
  }
  // Toggle the clicked one (open if it was closed)
  if (!isOpen) {
    sectionExpanded.value[key] = true
  }
}

// Sync local isCartOpen with cart store (for tutorial system)
watch(isCartOpen, (open) => {
  cartStore.setCartOpen(open)
})

// Reverse sync: external openers drive the drawer through the store
// (e.g. the immersive catalog's floating cart button).
watch(
  () => cartStore.isCartOpen,
  (open) => {
    if (open !== isCartOpen.value) isCartOpen.value = open
  }
)

// Normalized search query for filtering
const normalizedSearch = computed(() => searchQuery.value.trim().toLowerCase())

// Check if text matches search (returns true if no search or if matches)
function matchesSearch(text) {
  if (!normalizedSearch.value) return true
  return text.toLowerCase().includes(normalizedSearch.value)
}

// Clear search when navigating
function handleNavClick() {
  searchQuery.value = ''
  closeMenu()
}

const { isAdmin, isAgent } = storeToRefs(authStore)

const isCatalogRoute = computed(() => {
  const name = route.name
  return name === ROUTES.CATALOG || name === ROUTES.AGENTS_CATALOG
})

// Check if current route is an admin management route
const isAdminRoute = computed(() => route.path.startsWith('/app/admin'))

// Cart is available for: admin, client, agent - but hidden on admin routes
const canShowCart = computed(() => {
  const role = authStore.userRole
  const hasCartRole = role === 'admin' || role === 'client' || role === 'agent'
  return hasCartRole && !isAdminRoute.value
})

// Floating cart button on catalog, profile, promo detail, and discount pages
const showFloatingCartBtn = computed(() => {
  const name = route.name
  return (
    name === ROUTES.CATALOG ||
    name === ROUTES.AGENTS_CATALOG ||
    name === ROUTES.PROFILE ||
    name === ROUTES.PROMOTION_DETAIL ||
    name === ROUTES.AGENTS_PROMOTION_DETAIL ||
    name === ROUTES.QUANTITY_DISCOUNT ||
    name === ROUTES.AGENTS_QUANTITY_DISCOUNT
  )
})

// Wallet/kupa screens: hide the scroller's scrollbar rail (app-like field look;
// scrolling itself still works via touch/wheel)
const isWalletRoute = computed(() => {
  const name = route.name
  return name === ROUTES.AGENTS_WALLET || name === ROUTES.AGENTS_WALLET_PACKAGES
})

const isPCView = computed(() => catalogStore.isPCView)

function openCart() {
  isCartOpen.value = true
}

const userInitials = computed(() => {
  const name = authStore.userDisplayName || ''
  if (!name) return '?'
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}

function closeMenu() {
  menuOpen.value = false
}

async function handleLogout() {
  await authStore.logout()
  router.push({ name: ROUTES.HOME })
}

onMounted(() => {
  window.addEventListener('resize', catalogStore.handleWindowResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', catalogStore.handleWindowResize)
})
</script>

<template>
  <div class="app-shell">
    <!-- Header -->
    <header class="app-header">
      <div class="header-right">
        <button
          v-if="authStore.isAuthenticated"
          class="menu-toggle"
          aria-label="תפריט"
          @click="toggleMenu"
        >
          <svg
            class="menu-icon"
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M3 12h18M3 6h18M3 18h18" />
          </svg>
        </button>
        <div class="brand">
          <img src="@/assets/logo/logo_transparent.svg" alt="GoFood" class="brand-logo" />
          <h1 class="app-title">בחירת הקמעונאים</h1>
        </div>
      </div>
      <div class="header-left">
        <RouterLink
          v-if="authStore.isAuthenticated"
          :to="{ path: '/app/profile' }"
          class="user-badge"
        >
          <img
            v-if="authStore.userPhotoURL"
            :src="authStore.userPhotoURL"
            :alt="authStore.userDisplayName"
            class="user-avatar-sm user-avatar-img"
          />
          <div v-else class="user-avatar-sm">{{ userInitials }}</div>
          <span class="user-name">{{ authStore.userDisplayName }}</span>
        </RouterLink>
        <RouterLink v-else :to="{ name: ROUTES.LOGIN }" class="header-login-btn">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
            <polyline points="10 17 15 12 10 7" />
            <line x1="15" y1="12" x2="3" y2="12" />
          </svg>
          <span>התחבר</span>
        </RouterLink>
      </div>
    </header>

    <div class="app-body">
      <!-- Sidebar (hidden for guests) -->
      <aside v-if="authStore.isAuthenticated" class="app-sidebar" :class="{ open: menuOpen }">
        <!-- Sidebar Header -->
        <div class="sidebar-header">
          <div class="sidebar-brand">
            <img src="@/assets/logo/logo_transparent.svg" alt="GoFood" class="sidebar-logo" />
            <span class="sidebar-title">בחירת הקמעונאים</span>
          </div>
          <button class="sidebar-close" @click="closeMenu" aria-label="סגור תפריט">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- User Section (Gamified - Phase 2B) -->
        <UserSidebarSection :expanded="menuOpen" @navigate="closeMenu" />

        <!-- Search Section -->
        <SidebarSearch v-model="searchQuery" />

        <!-- Desktop Dashboard - Primary Action (Admin Only) -->
        <RouterLink
          v-if="isAdmin"
          v-show="matchesSearch('שולחן עבודה')"
          :to="{ name: ROUTES.ADMIN_DASHBOARD }"
          class="desktop-dashboard-link-wrapper"
          @click="handleNavClick"
        >
          <span class="desktop-dashboard-icon">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
            </svg>
          </span>
          <span class="desktop-dashboard-text">שולחן עבודה</span>
        </RouterLink>

        <!-- Navigation -->
        <nav class="sidebar-nav">
          <div
            v-show="
              matchesSearch('קטלוג') || matchesSearch('פרופיל') || matchesSearch('ההזמנות שלי')
            "
            class="nav-section"
          >
            <button
              v-show="matchesSearch('קטלוג')"
              type="button"
              class="nav-link nav-link-btn"
              :class="{ 'router-link-active': isCatalogRoute }"
              @click="openCatalogChooser"
            >
              <span class="nav-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" />
                </svg>
              </span>
              <span class="nav-text">קטלוג</span>
            </button>

            <RouterLink
              v-show="matchesSearch('פרופיל')"
              :to="{ name: ROUTES.PROFILE }"
              class="nav-link"
              @click="handleNavClick"
            >
              <span class="nav-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </span>
              <span class="nav-text">פרופיל</span>
            </RouterLink>

            <!-- My Orders - visible only for non-agent users -->
            <RouterLink
              v-if="!isAgent"
              v-show="matchesSearch('ההזמנות שלי')"
              :to="{ name: ROUTES.MY_ORDERS }"
              class="nav-link"
              @click="handleNavClick"
            >
              <span class="nav-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </span>
              <span class="nav-text">ההזמנות שלי</span>
            </RouterLink>
          </div>

          <!-- אפליקציות Section - client exclusive apps -->
          <!-- Temporarily hidden for all users. To restore: v-if="!isAgent && !isAdmin" -->
          <div
            v-if="false"
            v-show="matchesSearch('אפליקציות') || matchesSearch('מערכת שילוט')"
            class="nav-section client-apps-section"
          >
            <div class="client-apps-header">
              <div class="client-apps-badge">
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect x="3" y="3" width="7" height="7" rx="1.5" />
                  <rect x="14" y="3" width="7" height="7" rx="1.5" />
                  <rect x="3" y="14" width="7" height="7" rx="1.5" />
                  <rect x="14" y="14" width="7" height="7" rx="1.5" />
                </svg>
              </div>
              <div class="client-apps-titles">
                <span class="client-apps-title">אפליקציות</span>
                <span class="client-apps-subtitle">ללקוחות GoFood</span>
              </div>
            </div>

            <div class="client-apps-links">
              <RouterLink
                v-show="matchesSearch('מערכת שילוט')"
                :to="{ name: ROUTES.SIGNAGE_DASHBOARD }"
                class="nav-link client-app-link"
                @click="handleNavClick"
              >
                <span class="nav-icon">
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <rect x="2" y="3" width="20" height="14" rx="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                  </svg>
                </span>
                <span class="nav-text">מערכת שילוט</span>
                <span class="app-badge-new">חדש</span>
              </RouterLink>
            </div>
          </div>

          <!-- סוכנים Section (visible for agent and admin roles) -->
          <template v-if="isAgent || isAdmin">
            <div
              v-show="
                matchesSearch('פורטל סוכנים') ||
                matchesSearch('לקוחות') ||
                matchesSearch('תכנון מסלול') ||
                matchesSearch('יעדים') ||
                matchesSearch('סטטיסטיקות')
              "
              class="nav-section"
            >
              <div
                class="nav-section-header"
                :class="{ collapsible: isAdmin }"
                @click="isAdmin && toggleSection('agents')"
              >
                <span class="nav-section-title">סוכנים</span>
                <span
                  v-if="isAdmin"
                  class="section-chevron"
                  :class="{ expanded: sectionExpanded.agents }"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>

              <div
                class="nav-section-links"
                :class="{ collapsed: isAdmin && !sectionExpanded.agents }"
              >
                <RouterLink
                  v-show="matchesSearch('פורטל סוכנים')"
                  :to="{ name: ROUTES.AGENTS_PORTAL }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
                    </svg>
                  </span>
                  <span class="nav-text">פורטל סוכנים</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('לקוחות')"
                  :to="{ name: ROUTES.AGENTS_CLIENTS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                    </svg>
                  </span>
                  <span class="nav-text">לקוחות</span>
                </RouterLink>

                <!-- Route Planning - Agent only -->
                <RouterLink
                  v-if="!isAdmin"
                  v-show="matchesSearch('תכנון מסלול')"
                  :to="{ name: ROUTES.AGENTS_ROUTE_PLANNING }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="10" r="3" />
                      <path
                        d="M12 2a8 8 0 00-8 8c0 5.4 7 11.5 7.3 11.8a1 1 0 001.4 0C13 21.5 20 15.4 20 10a8 8 0 00-8-8z"
                      />
                    </svg>
                  </span>
                  <span class="nav-text">תכנון מסלול</span>
                </RouterLink>

                <!-- Goals - Admin only -->
                <RouterLink
                  v-if="isAdmin"
                  v-show="matchesSearch('יעדים')"
                  :to="{ name: ROUTES.AGENTS_GOALS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <circle cx="12" cy="12" r="6" />
                      <circle cx="12" cy="12" r="2" />
                    </svg>
                  </span>
                  <span class="nav-text">יעדים</span>
                </RouterLink>

                <!-- Statistics -->
                <RouterLink
                  v-show="matchesSearch('סטטיסטיקות')"
                  :to="{ name: ROUTES.AGENTS_STATISTICS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <line x1="18" y1="20" x2="18" y2="10" />
                      <line x1="12" y1="20" x2="12" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="14" />
                    </svg>
                  </span>
                  <span class="nav-text">סטטיסטיקות</span>
                </RouterLink>
              </div>
            </div>

            <!-- סוכנים ניהול Section (Admin only) -->
            <div
              v-if="isAdmin"
              v-show="
                matchesSearch('אשף שכר') ||
                matchesSearch('קופה') ||
                matchesSearch('ארנק') ||
                matchesSearch('בקשות חופשה') ||
                matchesSearch('ניהול נוכחות') ||
                matchesSearch('ביצועי מכירות')
              "
              class="nav-section"
            >
              <div
                class="nav-section-header collapsible"
                @click="toggleSection('agentsManagement')"
              >
                <span class="nav-section-title">סוכנים ניהול</span>
                <span
                  class="section-chevron"
                  :class="{ expanded: sectionExpanded.agentsManagement }"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>

              <div
                class="nav-section-links"
                :class="{ collapsed: !sectionExpanded.agentsManagement }"
              >
                <!-- Salary Wizard -->
                <RouterLink
                  v-show="matchesSearch('אשף שכר')"
                  :to="{ name: ROUTES.ADMIN_SALARY_WIZARD }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect x="1" y="6" width="22" height="13" rx="2" />
                      <circle cx="12" cy="12.5" r="3" />
                      <line x1="5" y1="9" x2="5" y2="9.01" />
                      <line x1="19" y1="16" x2="19" y2="16.01" />
                    </svg>
                  </span>
                  <span class="nav-text">אשף שכר</span>
                </RouterLink>

                <!-- Wallet -->
                <RouterLink
                  v-show="matchesSearch('קופה') || matchesSearch('ארנק')"
                  :to="{ name: ROUTES.AGENTS_WALLET }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect x="2" y="6" width="20" height="14" rx="2" ry="2" />
                      <path d="M2 10h20" />
                      <path d="M16 14h2" />
                    </svg>
                  </span>
                  <span class="nav-text">קופה</span>
                </RouterLink>

                <!-- Vacation Requests -->
                <RouterLink
                  v-show="matchesSearch('בקשות חופשה')"
                  :to="{ name: ROUTES.ADMIN_AGENT_REQUESTS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M17 3a2.85 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </span>
                  <span class="nav-text">בקשות חופשה</span>
                </RouterLink>

                <!-- Attendance Management -->
                <RouterLink
                  v-show="matchesSearch('ניהול נוכחות')"
                  :to="{ name: ROUTES.ADMIN_ATTENDANCE }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                  </span>
                  <span class="nav-text">ניהול נוכחות</span>
                </RouterLink>

                <!-- Sales Analysis -->
                <RouterLink
                  v-show="matchesSearch('ביצועי מכירות')"
                  :to="{ name: ROUTES.ADMIN_SALES_ANALYSIS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <line x1="18" y1="20" x2="18" y2="10" />
                      <line x1="12" y1="20" x2="12" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="14" />
                      <polyline points="4 14 8 10 12 12 20 4" />
                      <polyline points="16 4 20 4 20 8" />
                    </svg>
                  </span>
                  <span class="nav-text">ביצועי מכירות</span>
                </RouterLink>
              </div>
            </div>

            <!-- B2B Section (Admin only) -->
            <div
              v-if="isAdmin"
              v-show="
                matchesSearch('דיוור') ||
                matchesSearch('ניהול הזמנות') ||
                matchesSearch('ניהול לידים') ||
                matchesSearch('ניהול קמפיינים')
              "
              class="nav-section"
            >
              <div class="nav-section-header collapsible" @click="toggleSection('b2b')">
                <span class="nav-section-title">B2B</span>
                <span class="section-chevron" :class="{ expanded: sectionExpanded.b2b }">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>

              <div class="nav-section-links" :class="{ collapsed: !sectionExpanded.b2b }">
                <RouterLink
                  v-show="matchesSearch('דיוור')"
                  :to="{ name: ROUTES.ADMIN_B2B_DIVUR }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M3 11l18-8-8 18-2-8-8-2z" />
                    </svg>
                  </span>
                  <span class="nav-text">דיוור</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('ניהול הזמנות')"
                  :to="{ name: ROUTES.ADMIN_ORDERS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  </span>
                  <span class="nav-text">ניהול הזמנות</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('ניהול לידים')"
                  :to="{ name: ROUTES.ADMIN_B2B_FORMS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                    </svg>
                  </span>
                  <span class="nav-text">ניהול לידים</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('ניהול קמפיינים')"
                  :to="{ name: ROUTES.ADMIN_CAMPAIGN_MANAGEMENT }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path
                        d="M2 9v6c0 .55.45 1 1 1h2v3h2v-3l11 4V5L7 9H3c-.55 0-1 .45-1 1zm14-2.61v11.22L7 14.61V9.39l9-3z"
                      />
                    </svg>
                  </span>
                  <span class="nav-text">ניהול קמפיינים</span>
                </RouterLink>
              </div>
            </div>
          </template>

          <template v-if="isAdmin">
            <!-- ניהול קטלוג Section -->
            <div
              v-show="
                matchesSearch('פריטים') ||
                matchesSearch('מחלקות') ||
                matchesSearch('ניהול מבצעים') ||
                matchesSearch('WhatsApp')
              "
              class="nav-section"
            >
              <div class="nav-section-header collapsible" @click="toggleSection('catalog')">
                <span class="nav-section-title">ניהול קטלוג</span>
                <span class="section-chevron" :class="{ expanded: sectionExpanded.catalog }">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>

              <div class="nav-section-links" :class="{ collapsed: !sectionExpanded.catalog }">
                <RouterLink
                  v-show="matchesSearch('פריטים')"
                  :to="{ name: ROUTES.ADMIN_CATALOG }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"
                      />
                      <line x1="7" y1="7" x2="7.01" y2="7" />
                    </svg>
                  </span>
                  <span class="nav-text">פריטים</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('מחלקות')"
                  :to="{ name: ROUTES.ADMIN_DEPARTMENTS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />
                    </svg>
                  </span>
                  <span class="nav-text">מחלקות</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('ניהול מבצעים')"
                  :to="{ name: ROUTES.ADMIN_PROMOTIONS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"
                      />
                      <line x1="7" y1="7" x2="7.01" y2="7" />
                    </svg>
                  </span>
                  <span class="nav-text">ניהול מבצעים</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('WhatsApp')"
                  :to="{ name: ROUTES.ADMIN_WHATSAPP }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path
                        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"
                      />
                    </svg>
                  </span>
                  <span class="nav-text">WhatsApp</span>
                </RouterLink>
              </div>
            </div>

            <!-- ניהול מערכת Section -->
            <div
              v-show="
                matchesSearch('נתונים') ||
                matchesSearch('ניהול משתמשים') ||
                matchesSearch('ניהול לקוחות') ||
                matchesSearch('ניקוד ורמות') ||
                matchesSearch('טפסי יצירת קשר') ||
                matchesSearch('קומיטים') ||
                matchesSearch('יומן הפיתוח') ||
                matchesSearch('אימיילים') ||
                matchesSearch('דיוור')
              "
              class="nav-section"
            >
              <div class="nav-section-header collapsible" @click="toggleSection('system')">
                <span class="nav-section-title">ניהול מערכת</span>
                <span class="section-chevron" :class="{ expanded: sectionExpanded.system }">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>

              <div class="nav-section-links" :class="{ collapsed: !sectionExpanded.system }">
                <RouterLink
                  v-show="matchesSearch('נתונים')"
                  :to="{ name: ROUTES.ADMIN_DATA }"
                  class="nav-link nav-link-primary"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="23 4 23 10 17 10" />
                      <polyline points="1 20 1 14 7 14" />
                      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                    </svg>
                  </span>
                  <span class="nav-text">נתונים</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('ניהול משתמשים')"
                  :to="{ name: ROUTES.ADMIN_USERS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                    </svg>
                  </span>
                  <span class="nav-text">ניהול משתמשים</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('ניהול לקוחות')"
                  :to="{ name: ROUTES.ADMIN_CLIENTS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
                    </svg>
                  </span>
                  <span class="nav-text">ניהול לקוחות</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('ניקוד ורמות')"
                  :to="{ name: ROUTES.ADMIN_LEVELS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polygon
                        points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                      />
                    </svg>
                  </span>
                  <span class="nav-text">ניקוד ורמות</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('טפסי יצירת קשר')"
                  :to="{ name: ROUTES.ADMIN_CONTACT_FORMS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                  </span>
                  <span class="nav-text">טפסי יצירת קשר</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('קומיטים')"
                  :to="{ name: ROUTES.ADMIN_COMMITS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <circle cx="12" cy="12" r="3" />
                      <circle cx="12" cy="5" r="1.5" />
                      <circle cx="19" cy="12" r="1.5" />
                      <circle cx="12" cy="19" r="1.5" />
                      <circle cx="5" cy="12" r="1.5" />
                      <line x1="12" y1="8" x2="12" y2="9" stroke-width="1.5" />
                      <line x1="15" y1="12" x2="16.5" y2="12" stroke-width="1.5" />
                      <line x1="12" y1="15" x2="12" y2="16" stroke-width="1.5" />
                      <line x1="8" y1="12" x2="6.5" y2="12" stroke-width="1.5" />
                    </svg>
                  </span>
                  <span class="nav-text">קומיטים</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('יומן הפיתוח')"
                  :to="{ name: ROUTES.ADMIN_DEVBLOG }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path d="M4 4h11a2 2 0 012 2v13a1 1 0 001 1H6a2 2 0 01-2-2V4z" />
                      <path d="M17 7h2a1 1 0 011 1v11a1 1 0 01-1 1" />
                      <line x1="7" y1="8" x2="12" y2="8" />
                      <line x1="7" y1="11" x2="12" y2="11" />
                      <line x1="7" y1="14" x2="10" y2="14" />
                    </svg>
                  </span>
                  <span class="nav-text">יומן הפיתוח</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('אימיילים')"
                  :to="{ name: ROUTES.ADMIN_EMAILS }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                      />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  <span class="nav-text">אימיילים</span>
                </RouterLink>

                <RouterLink
                  v-show="matchesSearch('דיוור')"
                  :to="{ name: ROUTES.ADMIN_MAILING }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                      />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  <span class="nav-text">ניהול מערכת דיוור</span>
                </RouterLink>
              </div>
            </div>

            <!-- אפל. לקוחות Section (Admin only) -->
            <div v-show="matchesSearch('מערכת שילוט')" class="nav-section">
              <div class="nav-section-header collapsible" @click="toggleSection('clientApps')">
                <span class="nav-section-title">אפל. לקוחות</span>
                <span class="section-chevron" :class="{ expanded: sectionExpanded.clientApps }">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>

              <div class="nav-section-links" :class="{ collapsed: !sectionExpanded.clientApps }">
                <RouterLink
                  v-show="matchesSearch('מערכת שילוט')"
                  :to="{ name: ROUTES.ADMIN_SIGNAGE_SELECT }"
                  class="nav-link"
                  @click="handleNavClick"
                >
                  <span class="nav-icon">
                    <svg
                      viewBox="0 0 24 24"
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect x="2" y="3" width="20" height="14" rx="2" />
                      <line x1="8" y1="21" x2="16" y2="21" />
                      <line x1="12" y1="17" x2="12" y2="21" />
                    </svg>
                  </span>
                  <span class="nav-text">מערכת שילוט</span>
                </RouterLink>
              </div>
            </div>
          </template>
        </nav>

        <!-- View Settings (only on catalog routes) -->
        <SidebarViewSettings
          v-if="isCatalogRoute"
          v-show="matchesSearch('תצוגה') || matchesSearch('הגדרות')"
        />

        <!-- Sidebar Footer -->
        <div class="sidebar-footer">
          <!-- Version Display -->
          <div class="version-display">
            <span class="version-label">גרסה:</span>
            <span class="version-number">{{ version }}</span>
          </div>

          <button class="logout-btn" @click="handleLogout">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            <span>התנתקות</span>
          </button>
        </div>

        <!-- Powered By Section -->
        <div class="powered-by">
          <div class="powered-by-content">
            <svg
              class="powered-by-icon"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
            <span class="powered-by-text">Powered by <strong>CyberPark</strong></span>
          </div>
        </div>
      </aside>

      <!-- Overlay -->
      <div v-if="menuOpen" class="sidebar-overlay" @click="closeMenu"></div>

      <!-- Main Content -->
      <main class="app-content" :class="{ 'app-content--no-scrollbar': isWalletRoute }">
        <RouterView v-slot="{ Component }">
          <KeepAlive :include="['AgentCatalogView', 'AdminOrdersView']">
            <component :is="Component" />
          </KeepAlive>
        </RouterView>
      </main>
    </div>

    <!-- Cart (Floating Button / Mobile Footer + Drawer) -->
    <template v-if="canShowCart">
      <!-- Desktop: floating draggable cart button -->
      <FloatingCartButton v-if="showFloatingCartBtn && isPCView" @open-cart="openCart" />
      <!-- Mobile/Tablet: fixed footer bar -->
      <MobileCartFooter v-if="showFloatingCartBtn && !isPCView" @open-cart="openCart" />
      <CartDrawer v-model="isCartOpen" />
    </template>

    <!-- Catalog mode chooser (normal vs immersive) -->
    <CatalogModeChooser
      :open="showCatalogChooser"
      @select-normal="chooseNormalCatalog"
      @select-immersive="chooseImmersiveCatalog"
      @close="showCatalogChooser = false"
    />
  </div>
</template>

<style scoped>
/* ============================================
   App Shell
   ============================================ */
.app-shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ============================================
   Header
   ============================================ */
.app-header {
  height: var(--header-height);
  background: var(--gradient-header);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--spacing-md);
  position: sticky;
  top: 0;
  z-index: 101;
}

.header-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.menu-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  color: white;
  transition: all var(--transition-fast);
}

.menu-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.brand-logo {
  width: 46px;
  height: 46px;
  border-radius: var(--border-radius);
  object-fit: contain;
}

.app-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-normal);
  color: white;
  margin: 0;
}

.user-badge {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 6px 12px 6px 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius-full);
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}

.user-avatar-sm {
  width: 28px;
  height: 28px;
  background: linear-gradient(180deg, #f5c96a 0%, #e8a849 50%, #d9923c 100%);
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  color: var(--text-primary-dark, #1a1a2e);
}

.user-avatar-img {
  object-fit: cover;
  background: none;
}

.user-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: rgba(255, 255, 255, 0.9);
}

.header-login-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 6px 8px;
  background: none;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  transition: color 0.15s;
}

.header-login-btn:hover {
  color: #fff;
}

/* ============================================
   App Body
   ============================================ */
.app-body {
  flex: 1;
  display: flex;
  position: relative;
}

.app-content {
  flex: 1;
  background: var(--bg-secondary);
  overflow-y: auto;
}

/* Wallet screens: no visible scrollbar rail (scrolling unaffected) */
.app-content--no-scrollbar {
  scrollbar-width: none; /* Firefox */
}

.app-content--no-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome / Safari / Edge */
}

/* ============================================
   Sidebar
   ============================================ */
.app-sidebar {
  width: var(--sidebar-width);
  background: var(--bg-primary);
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  transform: translateX(100%);
  transition: transform var(--transition-slow);
  box-shadow: var(--shadow-xl);
}

.app-sidebar.open {
  transform: translateX(0);
}

/* Sidebar Header */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  background: var(--gradient-header);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.sidebar-logo {
  width: 40px;
  height: 40px;
  border-radius: var(--border-radius);
  object-fit: contain;
}

.sidebar-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-normal);
  color: white;
}

.sidebar-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: var(--border-radius);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.sidebar-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  padding: var(--spacing-md);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.nav-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.nav-section-header {
  display: flex;
  align-items: center;
  padding: 0 var(--spacing-sm);
  margin-bottom: var(--spacing-xs);
}

.nav-section-header.collapsible {
  cursor: pointer;
  user-select: none;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius);
  transition: background var(--transition-fast);
}

.nav-section-header.collapsible:hover {
  background: rgba(255, 255, 255, 0.05);
}

.nav-section-title {
  font-size: 13px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 1;
}

.section-chevron {
  display: flex;
  align-items: center;
  color: var(--text-muted);
  transform: rotate(-90deg);
  transition: transform 0.2s ease;
}

.section-chevron.expanded {
  transform: rotate(0deg);
}

.nav-section-links {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  max-height: 500px;
  overflow: hidden;
  opacity: 1;
  transition:
    max-height 0.3s ease,
    opacity 0.2s ease;
}

.nav-section-links.collapsed {
  max-height: 0;
  opacity: 0;
  pointer-events: none;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  text-decoration: none;
  transition: all var(--transition-fast);
  position: relative;
}

/* Button variant of a nav link (e.g. the catalog mode chooser trigger) — reset
   native button styling so it matches the RouterLink siblings exactly. */
.nav-link-btn {
  width: 100%;
  background: transparent;
  border: none;
  font-family: var(--font-family);
  text-align: start;
  cursor: pointer;
}

.nav-link::before {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 0;
  background: var(--gradient-primary);
  border-radius: 0 3px 3px 0;
  transition: height var(--transition-fast);
}

.nav-link:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.nav-link:hover .nav-icon {
  color: var(--color-primary);
}

.nav-link.router-link-active {
  background: rgba(99, 102, 241, 0.1);
  color: var(--color-primary);
}

.nav-link.router-link-active::before {
  height: 24px;
}

.nav-link.router-link-active .nav-icon {
  color: var(--color-primary);
}

.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--text-muted);
  transition: color var(--transition-fast);
}

.nav-text {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

/* Client Apps Section - Special branded section */
.client-apps-section {
  margin-top: var(--spacing-sm);
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.04) 0%, rgba(51, 65, 85, 0.06) 100%);
  border: 1px solid rgba(30, 41, 59, 0.1);
  border-radius: var(--border-radius-lg, 12px);
  padding: var(--spacing-sm);
  position: relative;
  overflow: hidden;
}

.client-apps-section::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  background: var(--gradient-primary);
  border-radius: var(--border-radius-lg, 12px) var(--border-radius-lg, 12px) 0 0;
}

.client-apps-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-xs) var(--spacing-sm);
}

.client-apps-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--gradient-primary);
  color: white;
  flex-shrink: 0;
}

.client-apps-titles {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.client-apps-title {
  font-size: 13px;
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  letter-spacing: 0.3px;
}

.client-apps-subtitle {
  font-size: 11px;
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);
}

.client-apps-links {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.client-app-link {
  position: relative;
}

.app-badge-new {
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  color: white;
  background: var(--color-success);
  padding: 1px 6px;
  border-radius: 10px;
  margin-inline-start: auto;
  letter-spacing: 0.3px;
}

/* Desktop Dashboard Link - Primary Action */
.desktop-dashboard-link-wrapper {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  color: #334155;
  text-decoration: none;
  transition: all var(--transition-fast);
}

.desktop-dashboard-link-wrapper:hover {
  background: var(--bg-secondary);
}

.desktop-dashboard-link-wrapper.router-link-active {
  background: rgba(99, 102, 241, 0.1);
}

.desktop-dashboard-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #3b82f6;
  transition: color var(--transition-fast);
}

.desktop-dashboard-link-wrapper:hover .desktop-dashboard-icon {
  color: #2563eb;
}

.desktop-dashboard-text {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #334155;
}

.desktop-dashboard-link-wrapper:hover .desktop-dashboard-text {
  color: #1e293b;
}

/* Primary Nav Link - Important/Highlighted (Amber/Gold theme) */
.nav-link-primary {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(234, 179, 8, 0.1) 100%);
  border: 1px solid rgba(245, 158, 11, 0.4);
  margin-bottom: var(--spacing-sm);
}

.nav-link-primary .nav-icon {
  color: #d97706;
}

.nav-link-primary .nav-text {
  color: #b45309;
  font-weight: var(--font-weight-semibold);
}

.nav-link-primary:hover {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.18) 0%, rgba(234, 179, 8, 0.18) 100%);
  border-color: #f59e0b;
}

.nav-link-primary.router-link-active {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.22) 0%, rgba(234, 179, 8, 0.22) 100%);
  border-color: #f59e0b;
}

.nav-link-primary.router-link-active .nav-icon,
.nav-link-primary.router-link-active .nav-text {
  color: #b45309;
}

/* Powered By Section */
.powered-by {
  padding: var(--spacing-sm) var(--spacing-md);
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  border-top: 1px solid rgba(99, 102, 241, 0.3);
  position: relative;
  overflow: hidden;
}

.powered-by::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.6), transparent);
  animation: scanline 3s linear infinite;
  will-change: transform;
}

@keyframes scanline {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.powered-by-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.powered-by-icon {
  color: #06b6d4;
  filter: drop-shadow(0 0 4px rgba(6, 182, 212, 0.5));
}

.powered-by-text {
  font-size: 10px;
  color: #94a3b8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.powered-by-text strong {
  color: #06b6d4;
  font-weight: var(--font-weight-semibold);
  text-shadow: 0 0 8px rgba(6, 182, 212, 0.4);
}

/* Sidebar Footer */
.sidebar-footer {
  padding: var(--spacing-md);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

/* Version Display */
.version-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs);
  font-size: 12px;
  color: var(--text-tertiary);
  background: var(--bg-tertiary);
  border-radius: var(--border-radius-sm);
}

.version-label {
  font-weight: var(--font-weight-medium);
}

.version-number {
  font-family: 'Courier New', monospace;
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
}

.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius);
  color: var(--color-error);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.logout-btn:hover {
  background: var(--color-error);
  color: white;
}

/* ============================================
   Overlay
   ============================================ */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 150;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* ============================================
   Responsive
   ============================================ */
@media (max-width: 1023px) {
  .user-name {
    display: none;
  }

  .user-badge {
    padding: 6px;
  }

  .brand-logo {
    width: 32px;
    height: 32px;
  }
}

/* ============================================
   Tablet Icon Grid (500px - 1023px)
   ============================================ */
@media (min-width: 500px) and (max-width: 1023px) {
  .nav-section {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--spacing-md);
  }

  .nav-section-header {
    grid-column: 1 / -1;
    margin-bottom: 0;
  }

  .nav-section-title {
    font-weight: 700;
    font-size: 15px;
    color: var(--color-primary-dark);
  }

  .nav-section-links {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--spacing-md);
  }

  .nav-link {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--spacing-sm);
    gap: var(--spacing-xs);
    border-radius: var(--border-radius);
  }

  .nav-link::before {
    display: none;
  }

  .nav-link.router-link-active {
    background: rgba(59, 130, 246, 0.1);
    color: var(--color-primary);
  }

  .nav-link.router-link-active .nav-icon,
  .nav-link.router-link-active .nav-text {
    color: var(--color-primary);
    font-weight: 600;
  }

  .nav-icon {
    color: var(--text-muted);
  }

  .nav-text {
    font-size: 12px;
    line-height: 1.2;
    color: var(--text-secondary);
  }

  .nav-link:hover {
    background: var(--bg-tertiary);
  }

  .nav-link-primary {
    margin-bottom: 0;
  }
}

@media (min-width: 700px) and (max-width: 1023px) {
  .nav-section {
    grid-template-columns: repeat(4, 1fr);
  }
  .nav-section-links {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 900px) and (max-width: 1023px) {
  .nav-section {
    grid-template-columns: repeat(5, 1fr);
  }
  .nav-section-links {
    grid-template-columns: repeat(5, 1fr);
  }
}
</style>
